import {
  APPOINTMENT_DEFAULT_DURATION,
  BUILDING_TYPE_LABELS,
  appointmentEndLabel,
  counterVisitLabel,
  displayName,
  FOLLOW_UP_STEP_LABELS,
  SEWER_NETWORK_TYPE_LABELS,
  townOf,
} from '@ghp/domain'
import { Tag, UrgentTag, cn, urgentSurface } from '@ghp/ui'
import { GitFork, MapPin, Phone, StickyNote, User } from 'lucide-react'

import type { CachedInquiry } from '../../../offline/data-db'
import { TOUR_STATE_PRESENTATION } from '../../inspections/labels'
import type { TourState } from '../../inspections/lib/list-filters'
import { placementStyle, type SlotPlacement } from '../lib/agenda-layout'
import s from './AppointmentCard.module.css'

/**
 * La carte d'un rendez-vous sur la gouttière — l'`cal-evt` de la maquette validée : l'heure et la
 * référence, l'adresse, le contact, puis le réseau et le type de contrôle.
 *
 * ⚠️ **Un `<button>`, pas une `<div>` cliquable.** C'est la cible d'ouverture de la pré-fiche : au
 * doigt comme au clavier, et annoncée comme une action. La maquette pose l'ouverture sur un
 * `data-editrdv` — un attribut ne se tabule pas.
 *
 * ⚠️ La géométrie est **en style en ligne**, et ne peut pas être ailleurs : elle se calcule par
 * carte (`placeDay`) depuis l'heure du rendez-vous et le nombre de colonnes de son groupe de
 * chevauchement. Tout le reste est au module CSS, en tokens.
 */
export interface AppointmentCardProps {
  inquiry: CachedInquiry
  placement: SlotPlacement
  /**
   * L'état du contrôle **dans la tournée** (`tourStateOf`), calculé une fois pour la grille.
   *
   * ⚠️ Pas `inquiry.status` : l'API cesse de servir une demande une fois déposée, donc son
   * statut en cache reste « Planifié » sur un contrôle que le bureau a déjà reçu (#181). La
   * carte n'a pas de quoi le corriger seule — le dépôt se lit dans la file d'envoi —, d'où
   * l'état en propriété plutôt qu'une lecture de plus par carte.
   */
  state: TourState
  /** Le créneau est fait (`isDone`, #577) : la carte s'estompe, sans rien perdre de lisible. */
  done: boolean
  onOpen: (inquiry: CachedInquiry) => void
}

export function AppointmentCard({ inquiry, placement, state, done, onOpen }: AppointmentCardProps) {
  const presentation = TOUR_STATE_PRESENTATION[state]
  const contact = inquiry.contact ?? null
  const phone = contact?.phone ?? null
  const name = contact === null ? null : displayName(contact)
  // `?? null` : une ligne en cache d'avant ces champs les relit `undefined` (`assignedInquirySchema`).
  const purpose = counterVisitLabel(inquiry.purpose ?? null, inquiry.followUpRank ?? null)
  const network = inquiry.sewerNetworkType ?? null
  // « Réseau unitaire · Maison · Systématique » : le type de bâti (#586) entre le réseau et le type.
  const building = inquiry.buildingType == null ? null : BUILDING_TYPE_LABELS[inquiry.buildingType]
  const networkLine = [
    network === null ? null : `Réseau ${SEWER_NETWORK_TYPE_LABELS[network].toLowerCase()}`,
    building,
    purpose,
  ]
    .filter((part) => part !== null)
    .join(' · ')
  // Le bureau a laissé une note (#575) — `?? ''` : une ligne en cache d'avant #433 la relit `undefined`.
  const noted = (inquiry.comment ?? '') !== ''
  // La fin du rendez-vous (#431), au fuseau de Paris ; rien sans rendez-vous.
  const end =
    inquiry.scheduledAt == null
      ? null
      : appointmentEndLabel(
          inquiry.scheduledAt,
          inquiry.appointmentDurationMinutes ?? APPOINTMENT_DEFAULT_DURATION,
        )

  return (
    <button
      type="button"
      // La case « Urgent » du bureau (BACK-OFFICE-X). `=== true` : une ligne en cache d'avant le
      // champ le relit `undefined`.
      className={cn(s.card, inquiry.urgent === true && urgentSurface)}
      style={placementStyle(placement)}
      data-done={done ? '' : undefined}
      onClick={() => {
        onOpen(inquiry)
      }}
    >
      {inquiry.urgent === true && <UrgentTag />}
      {/* `${p.rdv} · #${p.id}` de la maquette : l'heure et la référence sur une ligne. */}
      <span className={s.head}>
        {/* Le repère de la note (#575), en tête de ligne : l'en-tête d'une carte étroite se
            coupe à droite, et l'emporterait. Le texte reste sur la pré-fiche. */}
        {noted && (
          <StickyNote size={12} className={s.note} role="img" aria-label="Note du bureau" />
        )}
        <span className={s.time}>
          {placement.time}
          {end === null ? '' : ` → ${end}`}
        </span>{' '}
        · {inquiry.reference ?? 'Sans référence'}
      </span>
      <span className={s.address}>
        <MapPin size={13} aria-hidden />
        <span className={s.addressText}>{inquiry.address.street ?? 'Adresse inconnue'}</span>
      </span>
      <span className={s.lines}>
        {/* ⚠️ Ce qu'on sait du contact, jamais rien d'inventé — **la règle de la pré-fiche**
            (`OpeningSheet`) : le nom seul quand le numéro manque (un import client sans
            téléphone est courant, et c'est le seul nom sous lequel se présenter), le numéro seul
            quand le nom manque (noté sur place, #268). Deux écrans qui ouvrent le même contrôle
            ne peuvent pas répondre différemment sur ce point. L'icône suit ce que la ligne
            permet : on ne compose pas un nom.
            ⚠️ Le numéro et le nom sont deux morceaux : sur une carte étroite, c'est le nom qui
            cède la place, jamais le numéro (point 1.13 de la réunion GHP). */}
        {(phone !== null || name !== null) && (
          <span className={s.line}>
            {phone === null ? <User aria-hidden /> : <Phone aria-hidden />}
            {phone !== null && <span className={s.phone}>{phone}</span>}
            {name !== null && <span className={s.name}>{phone === null ? name : `· ${name}`}</span>}
          </span>
        )}
        {/* La ligne `git-fork` de la maquette : « Réseau unitaire · Systématique » — le réseau
            **du dossier** (`Site.sewer_network_type`, M43), pas celui que l'agent constatera. Ce
            qu'on en sait, comme le contact : le type seul quand le dossier tait le réseau, le
            réseau seul quand le type manque, et aucune ligne plutôt qu'une icône sans rien à
            côté. */}
        {networkLine !== '' && (
          <span className={s.line}>
            <GitFork aria-hidden />
            {networkLine}
          </span>
        )}
        <span className={s.line}>
          {townOf(inquiry.address) ?? 'Commune inconnue'} ·
          <span className={s.state} data-tone={presentation.tone}>
            {presentation.label}
          </span>
          {/* Le RDV1 ou le RDV2 qu'impose la suite d'un avis (#574), sur la ligne courte de l'état :
              la tête, elle, se coupe sur une colonne étroite. `!= null` : une ligne en cache
              d'avant le champ le relit `undefined`. */}
          {inquiry.appointmentImposedStep != null && (
            <Tag size="sm" tone="blue">
              {FOLLOW_UP_STEP_LABELS[inquiry.appointmentImposedStep]}
            </Tag>
          )}
        </span>
      </span>
    </button>
  )
}
